"use client"

import * as React from "react"
import { useQuery, type QueryKey } from "@tanstack/react-query"
import { UnifiedDataTable } from "./unified-data-table"
import { LiveIndicator } from "@/components/shared/data-table/live-indicator"
import type { UnifiedDataTableProps } from "./types"

/** Server response envelope. Either a bare array or { data, total, page, limit, totalPages }. */
export type LiveQueryResult<TData> =
  | TData[]
  | {
      data: TData[]
      total?: number
      page?: number
      limit?: number
      totalPages?: number
    }

interface LiveUnifiedDataTableProps<TData extends { id: string }, TValue = unknown>
  extends Omit<UnifiedDataTableProps<TData, TValue>, "data" | "pagination"> {
  /** React Query key — uniquely identifies the cache entry. */
  queryKey: QueryKey
  /** Fetcher returning either an array or { data, total, page, limit, totalPages }. */
  queryFn: () => Promise<LiveQueryResult<TData>>
  /** Polling cadence in ms. Default 30 000. Pass 0 to disable. */
  pollMs?: number
  /**
   * Optional predicate — when it returns true, polling pauses.
   * Receives the current data array.
   */
  pauseWhen?: (data: TData[]) => boolean
  /**
   * Said in the pill instead of "Live updates off" once polling stops, so a
   * consumer can give the reason (e.g. the campaign ended). Ignored while live.
   */
  pausedLabel?: string
  /** Optional pagination state passed through to UnifiedDataTable. */
  paginationState?: {
    page: number
    pageSize: number
    onPageChange: (page: number) => void
    onPageSizeChange: (size: number) => void
  }
}

/**
 * LiveUnifiedDataTable — drop-in superset of UnifiedDataTable that owns
 * its own data fetch + polling loop + visible "Auto-refresh 30 s" pill.
 *
 * The pill sits in the table's toolbar, beside any `renderToolbarRight`
 * items, with a "Refresh now" button. Its wording says whether polling is ON,
 * not whether a request happens to be in flight (PROD-888); only its icon
 * spins for a fetch.
 *
 * Use whenever a table needs to reflect live server state (campaign sends,
 * training enrolments, queue depth, sync runs, etc.).
 */
export function LiveUnifiedDataTable<TData extends { id: string }, TValue = unknown>({
  queryKey,
  queryFn,
  pollMs = 30_000,
  pauseWhen,
  pausedLabel,
  paginationState,
  renderToolbarRight,
  ...rest
}: LiveUnifiedDataTableProps<TData, TValue>) {
  const finalKey = paginationState
    ? [...queryKey, paginationState.page, paginationState.pageSize]
    : queryKey

  const query = useQuery({
    queryKey: finalKey,
    queryFn: async () => {
      const res = await queryFn()
      return Array.isArray(res) ? { data: res } : res
    },
    refetchInterval: (q) => {
      const current = q.state.data
      const rows = (current?.data ?? []) as TData[]
      if (!pollMs) return false
      if (rows.length > 0 && pauseWhen?.(rows)) return false
      return pollMs
    },
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
  })

  const rows: TData[] = query.data?.data ?? []
  const paused = pollMs === 0 || (rows.length > 0 && (pauseWhen?.(rows) ?? false))
  const total = query.data?.total ?? rows.length

  const pagination = paginationState
    ? {
        page: paginationState.page,
        pageSize: paginationState.pageSize,
        total,
        totalPages:
          query.data?.totalPages ?? Math.max(1, Math.ceil(total / paginationState.pageSize)),
        onPageChange: paginationState.onPageChange,
        onPageSizeChange: paginationState.onPageSizeChange,
      }
    : undefined

  const fillHeight = (rest as UnifiedDataTableProps<TData, TValue>).fillHeight

  return (
    <div className={fillHeight ? "relative flex-1 min-h-0 flex flex-col" : "relative"}>
      <UnifiedDataTable<TData, TValue>
        {...(rest as UnifiedDataTableProps<TData, TValue>)}
        data={rows}
        pagination={pagination}
        renderToolbarRight={(table) => (
          <>
            <LiveIndicator
              active={!paused}
              fetching={query.isFetching}
              pollMs={pollMs}
              pausedLabel={pausedLabel}
              onRefresh={() => void query.refetch()}
            />
            {renderToolbarRight?.(table)}
          </>
        )}
      />
    </div>
  )
}
